import React, { createContext, useContext, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Fade from "@mui/material/Fade";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import theme from "../theme";

// Create a context for the Modal
const ModalContext = createContext();

// Custom styled Modal Box
const StyledModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 500,
  backgroundColor: "#fff",
  borderRadius: "8px",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  padding: "16px",
  outline: "none",
  animation: "fadeIn 0.3s ease-in-out",
  "@keyframes fadeIn": {
    from: { opacity: 0, transform: "translate(-50%, -60%)" },
    to: { opacity: 1, transform: "translate(-50%, -50%)" },
  },
}));

// Custom styled Button
const StyledButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: "4px",
  padding: "8px 16px",
  fontWeight: 500,
  textTransform: "none",
  backgroundColor:
    variant === "cancel" ? theme.palette.grey[300] : theme.palette.primary.main,
  color: variant === "cancel" ? theme.palette.grey[800] : "#fff",
  "&:hover": {
    backgroundColor:
      variant === "cancel"
        ? theme.palette.grey[400]
        : theme.palette.primary.main,
  },
}));

// Modal Provider Component
export const ModalProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [dialogBox, setDialogBox] = useState({
    id: "",
    titleMessage: "",
    confirmMessage: "",
    handleConfirm: () => {},
    severity: "",
  });

  const showModal = ({
    id,
    titleMessage,
    confirmMessage,
    handleConfirm,
    severity = "",
  }) => {
    setDialogBox({
      id,
      titleMessage,
      confirmMessage,
      handleConfirm,
      severity,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDialogBox({
      id: "",
      titleMessage: "",
      confirmMessage: "",
      handleConfirm: () => {},
      severity: "",
    });
  };

  const handleConfirm = () => {
    dialogBox.handleConfirm();
    handleClose();
  };

  const getIcon = (severity) => {
    switch (severity) {
      case "success":
        return <CheckCircleIcon sx={{ color: "success.main", fontSize: 28 }} />;
      case "error":
        return <ErrorIcon sx={{ color: "error.main", fontSize: 28 }} />;
      case "warning":
        return <WarningIcon sx={{ color: "warning.main", fontSize: 28 }} />;
      case "info":
        return <HelpRoundedIcon sx={{ color: "info.main", fontSize: 28 }} />;
      default:
        return null;
    }
  };

  return (
    <ModalContext.Provider value={{ showModal }}>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
      >
        <StyledModalBox>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            {getIcon(dialogBox.severity)}

            <Typography
              id="modal-title"
              variant="h6"
              component="h2"
              sx={{ fontWeight: 600 }}
            >
              {dialogBox.titleMessage}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 0,
                right: -0.8,
                color: "#fff", // White icon for contrast
                bgcolor: theme.palette.primary.main, // Background color for the corner
                padding: 0, // No padding
                "&:hover": {
                  bgcolor: theme.palette.primary.main, // Darker shade on hover
                },
                width: 26, // Fixed size to fit snugly
                height: 26,
                borderRadius: "0 7px 0 10px", // Rounded only top-left to match modal
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} /> {/* Adjust icon size */}
            </IconButton>
          </Box>
          <Typography
            id="modal-description"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            {dialogBox.confirmMessage}
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
          >
            <Button
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 1,
                color: (theme) => theme.palette.text.primary,
                borderColor: (theme) => theme.palette.divider,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.action.hover,
                  borderColor: (theme) => theme.palette.primary.main,
                },
              }}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <StyledButton variant="confirm" onClick={handleConfirm}>
              Confirm
            </StyledButton>
          </Box>
        </StyledModalBox>
      </Modal>
      {children}
    </ModalContext.Provider>
  );
};

// Custom hook to use the Modal
export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
export default ModalProvider;
